import { PAGE_MS, REDUCED_MS, HOLE_RATIO, NEW_PAGE_SCALE, EASE_FORWARD, EASE_BACK, coverRadius } from './portalMotion';

// The scoped counterpart of portalTransitions.css. It uses the same departure, clock,
// aperture and lock as route navigation, but never snapshots or transforms the inbox/chrome.
// Registered CSS numbers interpolate the reveal continuously (no sampled clip/ring drift).
let registered = false;
function registerReveal() {
  if (registered || !window.CSS?.registerProperty) return;
  try { window.CSS.registerProperty({ name: '--pr-surface-reveal', syntax: '<number>', inherits: false, initialValue: '0' }); } catch { /* already registered by HMR */ }
  registered = true;
}

export function animatePortalSurface({ surface: { region, live }, origin, departure, change, back, reduced }) {
  registerReveal();
  const animations = [];
  const saved = { zIndex: live.style.zIndex, transformOrigin: live.style.transformOrigin, clipPath: live.style.clipPath, transform: live.style.transform };
  const ghost = live.cloneNode(true);
  ghost.setAttribute('aria-hidden', 'true');
  ghost.setAttribute('inert', '');
  ghost.dataset.prGhost = '';
  ghost.removeAttribute('id');
  ghost.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
  const held = getComputedStyle(live).transform;
  Object.assign(ghost.style, { position: 'absolute', inset: '0', width: `${region.clientWidth}px`, height: `${region.clientHeight}px`, maxWidth: 'none', margin: '0', zIndex: back ? '4' : '1', pointerEvents: 'none', transform: held, transformOrigin: `${origin.x}px ${origin.y}px` });
  region.appendChild(ghost);
  const sourceScrollers = live.querySelectorAll('[data-scroll]');
  ghost.querySelectorAll('[data-scroll]').forEach((el, i) => { el.scrollTop = sourceScrollers[i]?.scrollTop || 0; el.scrollLeft = sourceScrollers[i]?.scrollLeft || 0; });
  const ring = departure.ring;
  const hole = parseFloat(ring.style.width) / 2 * HOLE_RATIO;
  const cover = coverRadius(origin.x, origin.y, region.clientWidth, region.clientHeight);
  const duration = reduced || document.hidden || !live.animate || !window.CSS?.registerProperty ? REDUCED_MS : PAGE_MS;
  let resolve, timer, done = false;
  const finished = new Promise(r => { resolve = r; });
  const clean = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    animations.forEach(a => a.cancel());
    ghost.remove();
    Object.assign(live.style, saved);
    resolve();
  };
  const animate = (el, frames, options) => {
    const a = el.animate?.(frames, options);
    if (a) animations.push(a);
    return a;
  };
  try {
    change();
    Object.assign(saved, { zIndex: live.style.zIndex, transformOrigin: live.style.transformOrigin, clipPath: live.style.clipPath, transform: live.style.transform });
    live.style.zIndex = back ? '1' : '3';
    live.style.transformOrigin = `${origin.x}px ${origin.y}px`;
    // Start synchronously with the DOM commit: no fully revealed one-frame flash.
    if (duration === REDUCED_MS) {
      ring.style.visibility = 'hidden';
      animate(back ? ghost : live, back ? [{ opacity: 1 }, { opacity: 0 }] : [{ opacity: 0 }, { opacity: 1 }], { duration, fill: 'both' });
    } else {
      const easing = back ? EASE_BACK : EASE_FORWARD;
      const reveal = back ? ghost : live;
      const scale = `calc(${NEW_PAGE_SCALE} - ${NEW_PAGE_SCALE - 1} * var(--pr-surface-reveal))`;
      const radius = `calc((${hole}px + ${cover - hole}px * var(--pr-surface-reveal)) / ${scale})`;
      reveal.style.transform = `scale(${scale})`;
      reveal.style.clipPath = `circle(${radius} at ${origin.x}px ${origin.y}px)`;
      animate(reveal, [{ '--pr-surface-reveal': back ? '1' : '0' }, { '--pr-surface-reveal': back ? '0' : '1' }], { duration, easing, fill: 'both' });
      if (back) {
        animate(live, [{ transform: 'scale(.92)', filter: 'brightness(.45)' }, { transform: 'none', filter: 'brightness(1)' }], { duration, easing, fill: 'both' });
      } else {
        animate(ghost, [{ transform: held }, { transform: 'perspective(900px) rotateX(38deg) rotateY(-28deg) rotateZ(-200deg) scale(.04)' }], { duration, easing, fill: 'both' });
        animate(ghost, [{ opacity: 1 }, { opacity: 0 }], { duration: duration / 2, delay: duration / 2, fill: 'both' });
      }
      animate(ring, [{ transform: `scale(${back ? cover / hole : 1})` }, { transform: `scale(${back ? 1 : cover / hole})` }], { duration, easing, fill: 'both' });
      animate(ring, back ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], { duration: duration * .3, delay: back ? 0 : duration * .7, fill: 'both' });
    }
    timer = setTimeout(clean, duration + 40);
  } catch (error) {
    clean();
    throw error;
  }
  return { finished, skipTransition: clean };
}
